import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { useSeo } from "@/lib/seo";
import Reveal from "@/components/site/Reveal";
import { services, valueProps, industries } from "@/data/services";

const HERO_IMG =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxjb2xsYWJvcmF0aXZlJTIwb2ZmaWNlJTIwbWVldGluZyUyMGRpdmVyc2V8ZW58MHx8fHwxNzgyMzk4NzA2fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  useSeo({
    title: "HR Consultancy for Ontario Businesses",
    description:
      "The People Collective is an Ontario-based HR consultancy. We work alongside your team on policy, labour relations, leadership coaching, and strategic HR.",
    path: "/",
  });

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-24 md:pt-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#82917B]/30 rounded-full bg-[#F3EFE7] text-[#5A6358] text-xs tracking-[0.2em] uppercase">
                <Sparkles size={12} />
                Ontario HR Consultancy
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <h1
                data-testid="hero-headline"
                className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#2B3329]"
              >
                Working alongside you to{" "}
                <span className="italic text-[#82917B]">build stronger teams.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-7 max-w-xl text-lg md:text-xl leading-relaxed text-[#4A5348]">
                Practical, human-first HR support for new businesses, growing
                teams, and newly unionized workplaces across Ontario — policy,
                bargaining, leadership coaching, and the day-to-day people work.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-book"
                  className="tpc-btn tpc-btn-primary"
                >
                  Book a Consultation
                  <ArrowRight size={16} />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-services"
                  className="tpc-btn tpc-btn-secondary"
                >
                  Explore Services
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.18}>
              <div className="relative">
                <div className="absolute -inset-3 bg-[#F3EFE7] -z-10" />
                <img
                  src={HERO_IMG}
                  alt="A collaborative team meeting in a bright Ontario workspace"
                  loading="eager"
                  className="w-full h-[420px] md:h-[520px] object-cover"
                />
                <div className="absolute bottom-4 left-4 bg-[#FDFBF7] px-4 py-3 border border-[#82917B]/30 max-w-[260px]">
                  <p className="tpc-eyebrow">Trusted by</p>
                  <p className="font-serif text-lg text-[#2B3329] mt-1">
                    Ontario teams of 5 to 500
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
        <div className="tpc-divider" />
      </section>

      {/* VALUE PROPS */}
      <section className="py-24 md:py-32 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="tpc-eyebrow">Our approach</p>
                <h2 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
                  HR that respects the humans on both sides of the desk.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-6">
              {valueProps.map((v, i) => (
                <Reveal key={v.title} delay={i * 0.08}>
                  <div
                    data-testid={`value-prop-${i}`}
                    className="h-full p-7 border border-[#82917B]/20 bg-[#F3EFE7]/60 hover:bg-[#F3EFE7] transition-colors"
                  >
                    <p className="font-serif text-2xl text-[#2B3329]">
                      {v.title}
                    </p>
                    <p className="mt-3 text-[#4A5348] leading-relaxed text-[15px]">
                      {v.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 md:py-32 bg-[#F3EFE7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <Reveal>
                <p className="tpc-eyebrow">Services</p>
                <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight">
                  What we work on, together.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <Link
                to="/services"
                data-testid="home-services-all"
                className="tpc-link"
              >
                See all services <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((s, i) => {
              const Icon = s.Icon;
              return (
                <Reveal key={s.slug} delay={i * 0.05}>
                  <Link
                    to="/services"
                    data-testid={`home-service-${s.slug}`}
                    className="tpc-card group block h-full"
                  >
                    <Icon size={28} className="text-[#82917B]" />
                    <h3 className="mt-6 font-serif text-2xl text-[#2B3329]">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-[#4A5348] text-[15px] leading-relaxed">
                      {s.short}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm text-[#2B3329]">
                      Learn more
                      <ArrowRight
                        size={14}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES PREVIEW */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="tpc-eyebrow">Who we serve</p>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight">
                Built for the messy, in-between moments of growth.
              </h2>
              <p className="mt-6 text-[#4A5348] text-lg leading-relaxed">
                From your first hire to your first collective agreement, we
                meet you exactly where you are.
              </p>
              <Link
                to="/industries"
                data-testid="home-industries-all"
                className="mt-8 inline-flex tpc-link"
              >
                Explore industries served <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {industries.map((ind, i) => (
              <Reveal key={ind.slug} delay={i * 0.06}>
                <div
                  data-testid={`home-industry-${ind.slug}`}
                  className="p-6 border border-[#82917B]/20 bg-[#FDFBF7] h-full"
                >
                  <p className="tpc-eyebrow text-[#82917B]">0{i + 1}</p>
                  <p className="mt-3 font-serif text-2xl text-[#2B3329]">
                    {ind.title}
                  </p>
                  <p className="mt-2 text-[#4A5348] text-[15px] leading-relaxed">
                    {ind.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-24 md:py-32 bg-[#2B3329] text-[#E9E5DC]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <p className="tpc-eyebrow text-[#A6B19E]">In their words</p>
            <blockquote className="mt-6 font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight">
              “They didn’t just hand us policies. They sat with our managers,
              translated the legal language into our reality, and stayed long
              enough that we could do it on our own.”
            </blockquote>
            <p className="mt-8 text-[#C9CCC1]">
              Operations Director, mid-sized manufacturer, Southwestern Ontario
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-20 md:py-28 bg-[#F3EFE7]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2B3329]">
              Let’s build a stronger team together.
            </h2>
            <p className="mt-5 text-[#4A5348] text-lg max-w-2xl mx-auto">
              Free 30-minute consultation. We’ll listen, ask honest questions,
              and tell you whether we’re actually the right partner for the work.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="cta-band-book"
                className="tpc-btn tpc-btn-primary"
              >
                Book a Consultation <ArrowRight size={16} />
              </Link>
              <Link
                to="/about"
                data-testid="cta-band-about"
                className="tpc-btn tpc-btn-secondary"
              >
                Meet the Collective
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
